import React from 'react';
import { Stack, TableCell, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import {
  rowHoverBg,
  selectedRowBg,
  selectedRowBgOpaque,
} from '../../utils/rowHighlight';

export const MANAGEMENT_TABLE_HEADER_COLOR = '#4da7db';
// Kept for backward-compat (light values); prefer the theme-aware helpers.
export const MANAGEMENT_TABLE_SELECTED_COLOR = '#e0f7fa';
export const MANAGEMENT_TABLE_HOVER_COLOR = '#f5f5f5';

const stickyActionCellSx: SxProps<Theme> = {
  whiteSpace: 'nowrap',
  position: 'sticky',
  right: -16,
  zIndex: '1 !important',
};

type ManagementTableHeaderCellProps = {
  children: React.ReactNode;
  sticky?: boolean;
  align?: 'left' | 'center' | 'right';
};

/** Shared header cell used by the management tables. */
export const ManagementTableHeaderCell = ({
  children,
  sticky = false,
  align,
}: ManagementTableHeaderCellProps) => (
  <TableCell
    sx={
      sticky
        ? stickyActionCellSx
        : {
            whiteSpace: 'nowrap',
            // Paint the cell (not just the row) so the header color also shows
            // under a `stickyHeader` table, where MUI repaints head cells.
            backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR,
          }
    }
  >
    <Typography
      fontWeight="bold"
      color="#fff"
      bgcolor={sticky ? MANAGEMENT_TABLE_HEADER_COLOR : undefined}
      borderLeft={sticky ? 1 : undefined}
      textAlign={sticky ? 'center' : align}
      className={sticky ? '!px-2' : undefined}
    >
      {children}
    </Typography>
  </TableCell>
);

type ManagementTableActionCellProps = {
  children: React.ReactNode;
  selected?: boolean;
};

/** Shared sticky action cell used by the management tables. */
export const ManagementTableActionCell = ({
  children,
  selected = false,
}: ManagementTableActionCellProps) => (
  <TableCell
    sx={{
      ...stickyActionCellSx,
      // Sticky column needs an OPAQUE surface; follow the theme instead of #fff
      // (white block in dark mode) and use the opaque tint so scrolled columns
      // don't bleed through on a selected row.
      backgroundColor: selected ? selectedRowBgOpaque : 'background.paper',
    }}
  >
    <Stack
      direction="row"
      spacing={1}
      sx={{
        backgroundColor: selected ? selectedRowBgOpaque : 'background.paper',
        borderLeft: 1,
        borderColor: 'divider',
        px: 1,
      }}
    >
      {children}
    </Stack>
  </TableCell>
);

export const managementTableRowSx = (selected: boolean): SxProps<Theme> => ({
  '&:hover': { backgroundColor: rowHoverBg },
  backgroundColor: selected ? selectedRowBg : 'inherit',
});
